import type { DueItem } from "@kundeninfo/api-client";
import { heuteBeschriftung, tagBeschriftung, tageZwischen } from "../tickets/ticketFormat.js";

/**
 * TEXTE DER TERMINEBENE IN DER OBERFLÄCHE (Spec „Texte und Symbole“, D16, D23–D26; Plan T15, T30) — EINE Tabelle für
 * Quellen-Chips, Abschnitt „Termine“, Popup-Zeilen (kurz) und Vorlesestimme (ausgeschrieben); Schlüssel englisch, Werte
 * deutsch. Quellwörter und die
 * Vorlagen des Fälligkeitstickets (`ticketTitle`, `ticketDescription`) lauten wie im Server
 * (`packages/domain/src/dueItemTexts.ts`); beide Testdateien prüfen dieselben Beispiele (T30).
 */

type Source = DueItem["source"];
/** Was die Texte vom Eintrag lesen (wie `DueTextItem` im Server). */
export type DueTextItem = Pick<DueItem, "source" | "dateKind" | "reference" | "title" | "partner" | "companyShortCode"
  | "amount" | "currency" | "amountBasis" | "state" | "date" | "time" | "progress" | "ticket">;

const TITLE_MAX = 200;
const NONE = "–";

const SOURCE_WORD: Readonly<Record<Source, string>> = {
  TICKET: "Ticket", MILESTONE: "Meilenstein", OFFER: "Angebot", INVOICE: "Rechnung", PLANNED_CHARGE: "Zahlplan",
  SUPPLIER_PAYMENT: "Zahlung", SUPPLIER_NOTICE: "Kündigungsfrist",
};
const CHIP_LABEL: Readonly<Record<Source, string>> = {
  TICKET: "Tickets", MILESTONE: "Meilensteine", OFFER: "Angebote", INVOICE: "Rechnungen", PLANNED_CHARGE: "Zahlpläne",
  SUPPLIER_PAYMENT: "Zahlungen", SUPPLIER_NOTICE: "Kündigungsfristen",
};
const RESUBMIT_WORD = "Wiedervorlage";
/** Verb im Ticketdetail: nur Angebot, Rechnung und Zahlplan tragen eins (wie der Server). */
const TITLE_VERB: Readonly<Partial<Record<Source, string>>> = { OFFER: " läuft ab", INVOICE: " fällig", PLANNED_CHARGE: " fällig" };

const overdueSince = (n: number): string => `überfällig seit ${n} T`;
const STATE_TEXT = { dueToday: "heute fällig", open: "offen", done: "erledigt" } as const;
/** Montag zuerst (ISO 8601); auch die Namen des Kalenders (`CALENDAR_TEXT`). */
const WEEKDAYS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as const;
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November",
  "Dezember"] as const;

/** `2026-11-05` → `05.11.2026`; `03.11.` ohne Jahr. */
const dayText = (day: string): string => `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
const shortDay = (day: string): string => `${day.slice(8, 10)}.${day.slice(5, 7)}.`;

/** Das Quellwort eines Eintrags; eine Wiedervorlage heißt so (wie der Server). */
const wordOf = (i: Pick<DueItem, "source" | "dateKind">): string => (i.dateKind === "RESUBMIT" ? RESUBMIT_WORD : SOURCE_WORD[i.source]);

/** Das Tageswort (T15): „Fällig“, „Wiedervorlage“, „Gültig bis“, „Letzter Kündigungstag“, „Stichtag“. */
function dayWord(i: Pick<DueItem, "source" | "dateKind">): string {
  if (i.dateKind === "RESUBMIT") return RESUBMIT_WORD;
  if (i.source === "OFFER") return "Gültig bis";
  if (i.source === "SUPPLIER_NOTICE") return "Letzter Kündigungstag";
  return i.source === "MILESTONE" ? "Stichtag" : "Fällig";
}

function stateText(i: Pick<DueItem, "state" | "date">, heute: string): string {
  return i.state === "OVERDUE" ? overdueSince(tageZwischen(i.date, heute))
    : i.state === "DUE_TODAY" ? STATE_TEXT.dueToday : i.state === "OPEN" ? STATE_TEXT.open : STATE_TEXT.done;
}

/** „1.190,00 € brutto“ — Betrag über `Intl` (U+00A0 vor €), dahinter netto/brutto; ohne Betrag `null`. */
function amount(i: Pick<DueItem, "amount" | "currency" | "amountBasis">): string | null {
  if (i.amount === null) return null;
  const formatted = new Intl.NumberFormat("de-DE", { style: "currency", currency: i.currency ?? "EUR" }).format(i.amount);
  return i.amountBasis === null ? formatted : `${formatted} ${i.amountBasis === "GROSS" ? "brutto" : "netto"}`;
}

const ticketOther = (number: string, day: string | null): string =>
  (day === null ? `Ticket ${number}` : `Ticket ${number} · fällig ${shortDay(day)}`);

/** D26: am selben Tag nur „Ticket ‹Nummer›“, abweichend mit dem Tag des Tickets. */
function ticketLine(t: DueTextItem): string | null {
  if (t.ticket === null) return null;
  return t.ticket.dueDate === t.date ? `Ticket ${t.ticket.displayNumber}` : ticketOther(t.ticket.displayNumber, t.ticket.dueDate);
}

/** Popup-Zeilen (D24): Quellwort und Nummer, Titel, Partner · Unternehmen, Tag mit Zustand, Betrag, Fortschritt, Ticket. */
function popupLines(i: DueTextItem, heute: string): string[] {
  const day = `${tagBeschriftung(i.date)}${i.time === null ? "" : ` ${i.time}`}`;
  return [
    i.reference === null ? wordOf(i) : `${wordOf(i)} ${i.reference}`,
    i.title,
    [i.partner?.name ?? "", i.companyShortCode].filter((t) => t !== "").join(" · "),
    `${dayWord(i)} ${day} · ${stateText(i, heute)}`,
    amount(i),
    i.progress === null ? null : `${i.progress.done}/${i.progress.total} Tickets erledigt`,
    ticketLine(i),
  ].filter((z): z is string => z !== null && z !== "");
}

// ── Vorlesestimme (D23, G4): Langformen statt der Kurzformen des Popups — kein „Do“, „3 T“, „€“ oder „·“ ─────────────

/** „Donnerstag, 5. November“, in einem anderen Jahr als heute mit der Jahreszahl. */
function speakDay(day: string, heute: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const weekday = WEEKDAYS_LONG[(new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7]!;
  return `${weekday}, ${d}. ${MONTHS[m - 1]!}${day.slice(0, 4) === heute.slice(0, 4) ? "" : ` ${y}`}`;
}

function speakState(i: Pick<DueItem, "state" | "date">, heute: string): string {
  if (i.state !== "OVERDUE") return stateText(i, heute);
  const n = tageZwischen(i.date, heute);
  return `überfällig seit ${n} ${n === 1 ? "Tag" : "Tagen"}`;
}

/** „1.190,00 Euro brutto“ — die Währung als Wort. */
function speakAmount(i: Pick<DueItem, "amount" | "currency" | "amountBasis">): string | null {
  if (i.amount === null) return null;
  const formatted = new Intl.NumberFormat("de-DE", { style: "currency", currency: i.currency ?? "EUR", currencyDisplay: "name" })
    .format(i.amount);
  return i.amountBasis === null ? formatted : `${formatted} ${i.amountBasis === "GROSS" ? "brutto" : "netto"}`;
}

/** Vorlesestimme eines Eintrags (Tastaturwanderung der Zeitachse, Kalender), D23: „Rechnung RE-2026-0012, Muster GmbH, BS,
 *  1.190,00 Euro brutto, Fällig Mittwoch, 14. Oktober, überfällig seit 3 Tagen“ — dazu Titel, Uhrzeit, Fortschritt und
 *  Fälligkeitsticket wie im Popup, aber ausgeschrieben. */
function speak(i: DueTextItem, heute: string): string {
  const t = i.ticket;
  return [
    i.reference === null ? wordOf(i) : `${wordOf(i)} ${i.reference}`,
    i.title,
    i.partner?.name ?? "",
    i.companyShortCode,
    speakAmount(i),
    `${dayWord(i)} ${speakDay(i.date, heute)}${i.time === null ? "" : `, ${i.time} Uhr`}`,
    speakState(i, heute),
    i.progress === null ? null : `${i.progress.done} von ${i.progress.total} Tickets erledigt`,
    t === null ? null : t.dueDate === i.date || t.dueDate === null ? `Ticket ${t.displayNumber}`
      : `Ticket ${t.displayNumber}, fällig ${speakDay(t.dueDate, heute)}`,
  ].filter((z): z is string => z !== null && z !== "").join(", ");
}

const cut = (s: string): string => (s.length > TITLE_MAX ? `${s.slice(0, TITLE_MAX - 1)}…` : s);

export const DUE_ITEM_TEXT = {
  sourceWord: SOURCE_WORD,
  /** Wort einer Zeile: Quellwort, eine Wiedervorlage heißt „Wiedervorlage“. */
  wordOf,
  chipLabel: CHIP_LABEL,
  chipsName: "Quellen",
  sectionTitle: "Termine",
  sectionOverdue: (n: number): string => `${n} überfällig`,
  columns: { source: "Quelle", number: "Nummer", titlePartner: "Titel · Partner", company: "Unternehmen", due: "Fällig",
    amount: "Betrag", ticket: "Ticket" },
  none: NONE,
  empty: "Keine Termine.",
  overdueSince,
  ...STATE_TEXT,
  net: "netto",
  gross: "brutto",
  amount,
  createTicket: "Ticket anlegen",
  /** Hinweise im Panel „Aufgabe anlegen“ aus einer Fälligkeit — Wortlaut wie der Server (409 bzw. 404). */
  originTaken: "Zu dieser Fälligkeit gibt es bereits ein Ticket.",
  originMissing: "Diese Fälligkeit wurde nicht gefunden.",
  /** Ruling 496: „Ticket anlegen“ aus Zahlung oder Kündigungsfrist — privat in einem Team, dessen Mitglieder alle die
   *  Lieferantensicht haben (Hilfe am Teamfeld bzw. Hinweis, wenn es keines gibt). */
  supplierTeamHelp: "Privat im Team: wählbar sind Teams, deren Mitglieder alle die Lieferantensicht haben.",
  supplierTeamMissing: "Es gibt kein Team, dessen Mitglieder alle die Lieferantensicht haben – ein Ticket aus dieser "
    + "Fälligkeit lässt sich erst anlegen, wenn die Administration ein solches Team einrichtet.",
  /** Re-Review N3: „Meine Teams“ ist gescheitert — nicht dasselbe wie „kein geeignetes Team“. */
  teamsLoadError: "Die Teams konnten nicht geladen werden.",
  /** H10: im Ticketdialog, der sich nach 409 statt eines neuen Tickets öffnet. */
  originTakenOpened: "Zu dieser Fälligkeit gibt es bereits ein Ticket – es ist hier geöffnet.",
  /** Ereignis `CREATED` im Verlauf eines Tickets, das aus einer Fälligkeit entstand (von Hand bzw. vom Nachtlauf). */
  createdFromDue: "aus einer Fälligkeit angelegt",
  createdFromDueBySystem: "automatisch aus einer Fälligkeit angelegt",
  /** D26: „Ticket BS-T-0042 · fällig 03.11.“ — ein umgeplantes Fälligkeitsticket mit seinem Tag (ohne Tag nur die Nummer). */
  ticketOther,
  sectionLimit: "Mehr als 200 Termine – bitte Filter eingrenzen.",
  loadError: "Die Termine konnten nicht geladen werden.",
  newMilestone: "+ Meilenstein",
  /** Titel des Fälligkeitstickets, höchstens 200 Zeichen — Wortlaut wie der Server (Aufgabe 12). */
  ticketTitle(i: DueTextItem): string {
    const partner = i.partner === null ? "" : ` · ${i.partner.name}`;
    return cut(`${SOURCE_WORD[i.source]} ${i.reference ?? i.title}${TITLE_VERB[i.source] ?? ""}${partner}`);
  },
  /** Beschreibung des Fälligkeitstickets — Wortlaut wie der Server (Aufgabe 12). */
  ticketDescription(i: DueTextItem, heute: string): string {
    const formatted = amount(i);
    const lines = [`Angelegt aus einer Fälligkeit am ${dayText(heute)}.`, "", `- ${dayWord(i)}: ${heuteBeschriftung(i.date)}`];
    if (formatted !== null) lines.push(`- Betrag: ${formatted}`);
    lines.push(`- Unternehmen: ${i.companyShortCode}`);
    if (i.partner !== null) lines.push(`- Partner: ${i.partner.name}`);
    return lines.join("\n");
  },
  popupLines,
  speak,
  weekdaysLong: WEEKDAYS_LONG,
  months: MONTHS,
} as const;
